<template>
    <BaseCard padding="md" class="flex items-center justify-between gap-4">
        <div class="flex-1 min-w-0 flex flex-col gap-1">
            <div class="flex items-center gap-2 flex-wrap">
                <span class="body-semibold text-valence-stone-900 truncate">{{ campaign.name }}</span>
                <StatusChip :state="statusState">{{ statusLabel }}</StatusChip>
            </div>
            <div class="body-small text-valence-stone-500">
                {{ campaign.template.name }}
                <template v-if="campaign.is_self_assessment_only"> · Self-assessment only</template>
                · {{ campaign.cycle_count }} {{ campaign.cycle_count === 1 ? "participant" : "participants" }} · {{ campaign.submitted_response_count }}
                {{ campaign.submitted_response_count === 1 ? "response" : "responses" }}
            </div>
            <div v-if="campaign.has_report && campaign.report_generated_at" class="details text-valence-stone-500">
                Report Generated {{ formatDate(campaign.report_generated_at, "short") }}
                <span v-if="campaign.new_submissions_since_report > 0" class="text-valence-primary-600 body-small-bold">
                    · {{ campaign.new_submissions_since_report }} new since last report
                </span>
            </div>
            <div v-else-if="campaign.closes_at" class="details text-valence-stone-500">Closes {{ formatDate(campaign.closes_at, "short") }}</div>
            <div v-else class="details text-valence-stone-500">No report generated yet</div>
        </div>

        <div class="flex items-center gap-2 shrink-0">
            <button
                v-if="campaign.state === 'draft'"
                type="button"
                class="base-btn-neutral base-btn-square base-btn-sm inline-flex items-center gap-1.5"
                :disabled="busy"
                @click="emit('launch', campaign)">
                <i class="bi bi-send" aria-hidden="true"></i>
                {{ busy ? "Launching…" : "Launch" }}
            </button>
            <button
                v-if="campaign.state === 'active'"
                type="button"
                class="base-btn-neutral base-btn-square base-btn-sm inline-flex items-center gap-1.5"
                :disabled="busy"
                @click="emit('close', campaign)">
                <i class="bi bi-x" aria-hidden="true"></i>
                Close
            </button>
            <!-- LAB-988 dark CTA override — see .base-btn-dark in tailwind.css. -->
            <button type="button" class="base-btn-dark base-btn-sm" @click="emit('view', campaign.id)">View</button>
        </div>
    </BaseCard>
</template>

<script setup lang="ts">
import type { AdminCampaignSummary } from "~vue/api/generated/reflections-admin";
import BaseCard from "~vue/components/BaseCard.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { formatDate } from "~vue/utils/threeSixty";
import { computed } from "vue";

interface Props {
    campaign: AdminCampaignSummary;
    busy: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
    (event: "launch", campaign: AdminCampaignSummary): void;
    (event: "close", campaign: AdminCampaignSummary): void;
    (event: "view", campaignId: string): void;
}>();

const statusLabel = computed(() => {
    switch (props.campaign.state) {
        case "active":
            return "Active";
        case "draft":
            return "Draft";
        case "closed":
            return "Closed";
        default:
            return props.campaign.state;
    }
});

const statusState = computed(() => {
    switch (props.campaign.state) {
        case "active":
            return "active";
        case "draft":
            return "pending";
        case "closed":
            return "neutral";
        default:
            return "neutral";
    }
});
</script>
